import {
  BoxGeometry,
  CatmullRomCurve3,
  CircleGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  type Object3D,
  PlaneGeometry,
  Shape,
  ShapeGeometry,
  Vector3,
} from "three";
import type { AirportMap, GrassShade } from "../airport/AirportDefinition";
import { LAYER, PALETTE } from "../config/palette";
import {
  RUNWAY_SYMBOLS,
  SYMBOL_BADGE_COLOR,
  SYMBOL_SIZING,
} from "../config/symbols";
import {
  disposeChildren,
  extendPolyline,
  polygonGeometry,
  raggedRibbonGeometry,
  regularPolygonGeometry,
  resamplePolyline,
} from "./geometry";

const RIVER_EXTEND = 220;
const RIVER_SAMPLES = 120;
const RIVER_BANK_PADDING = 5.2;
const RIVER_BANK_JITTER = 1.75;
const SAILBOAT_INTERVAL = 90;
const SAILBOAT_DURATION = 142;
/** Longer than any real frame; beyond this the clock jumped, it did not tick. */
const MAX_SCENERY_GAP = 1;

/** Trees lean about a degree and a half, slowly and out of step. */
const TREE_SWAY_RADIANS = 0.027;
const TREE_SWAY_RATE = 0.52;
/** Pale bands riding the current, so the water is not a flat colour. */
const SHIMMER_COUNT = 8;
/** Fraction of the river travelled per second. */
const SHIMMER_SPEED = 0.022;
/** Cloud shadows: how many, how far they travel before wrapping, how fast. */
const CLOUD_COUNT = 5;
const CLOUD_SPAN = 380;
const CLOUD_SPEED = 2.4;
/** Birds cross in threes; each flock loops on its own line. */
const BIRD_FLOCKS = 3;
const BIRD_SPAN = 340;
const BIRD_SPEED = 11;
const BIRD_FLAP_RATE = 7.5;
/** Windsock at every threshold: it is the only wind the player can see. */
const WINDSOCK_SWAY = 0.26;
const WINDSOCK_RATE = 1.15;
/** How far off the runway axis the sock stands. */
const WINDSOCK_OFFSET = 9.5;

interface Sailboat {
  readonly group: Group;
  startedAt: number;
  readonly duration: number;
  readonly phase: number;
}

/** Anything animated off the scenery clock, with its own phase. */
interface Swaying {
  readonly group: Group;
  readonly phase: number;
}

interface Drifting {
  readonly object: Object3D;
  /** Unit direction of travel, and where the run starts. */
  readonly dirX: number;
  readonly dirY: number;
  readonly originX: number;
  readonly originY: number;
  /** Units already travelled when the clock was zero. */
  readonly offset: number;
  readonly speed: number;
  readonly span: number;
}

interface Flock {
  readonly group: Group;
  readonly wings: Mesh[];
  readonly drift: Drifting;
  readonly phase: number;
}

/**
 * Builds the static world: grass, apron, river, trees, buildings and runways.
 *
 * Everything flat uses unlit basic materials so the ground reads as solid
 * colour; only the props that need to look three dimensional (trees,
 * buildings, boats) take lambert shading.
 */
export class SceneryBuilder {
  readonly sceneryGroup = new Group();
  readonly runwayGroup = new Group();

  private readonly ground = new Mesh(
    new PlaneGeometry(512, 512),
    new MeshBasicMaterial({ color: PALETTE.ground }),
  );

  private runwaySymbols: Mesh[] = [];
  private symbolsVisible = false;
  private sailboats: Sailboat[] = [];
  private sailboatCurve: CatmullRomCurve3 | null = null;
  private nextSailboatAt = Number.POSITIVE_INFINITY;
  private sailboatsPausedAt: number | null = null;
  private lastUpdateAt: number | null = null;

  // The living background. All of it rides `clock`, which only advances while
  // the game runs, so a pause freezes the world rather than letting it skip
  // ahead — the boats need a shift instead because they carry a start time.
  private trees: Swaying[] = [];
  private windsocks: Swaying[] = [];
  private shimmer: Mesh[] = [];
  private clouds: Drifting[] = [];
  private flocks: Flock[] = [];
  private clock = 0;
  private clockAt: number | null = null;

  constructor() {
    this.sceneryGroup.name = "airport-scenery";
    this.runwayGroup.name = "airport-runways";
    this.ground.position.z = LAYER.ground;
  }

  get groundMesh(): Mesh {
    return this.ground;
  }

  build(map: AirportMap, nowSeconds: number): void {
    this.buildScenery(map, nowSeconds);
    this.buildRunways(map);
  }

  // ------------------------------------------------------------------ runways

  /** Colour-blind mode: shows the shape stamped on every runway threshold. */
  setSymbolsVisible(visible: boolean): void {
    this.symbolsVisible = visible;
    for (const symbol of this.runwaySymbols) {
      symbol.visible = visible;
    }
  }

  buildRunways(map: AirportMap): void {
    disposeChildren(this.runwayGroup);
    this.runwaySymbols = [];

    for (const runway of map.runways) {
      const midX = (runway.approach.x + runway.end.x) / 2;
      const midY = (runway.approach.y + runway.end.y) / 2;
      const angle = Math.atan2(
        runway.end.y - runway.approach.y,
        runway.end.x - runway.approach.x,
      );
      const length = Math.hypot(
        runway.end.x - runway.approach.x,
        runway.end.y - runway.approach.y,
      );
      const colors = PALETTE.runway[runway.color];

      const base = new Mesh(
        new PlaneGeometry(length, runway.width),
        new MeshBasicMaterial({ color: colors.base }),
      );
      base.position.set(midX, midY, LAYER.runway);
      base.rotation.z = angle;

      const centerline = new Mesh(
        new PlaneGeometry(length * 0.92, runway.width * 0.1),
        new MeshBasicMaterial({ color: colors.center }),
      );
      centerline.position.set(midX, midY, LAYER.runway + 0.02);
      centerline.rotation.z = angle;

      // Threshold block at the approach gate: the target the player aims for.
      const threshold = new Mesh(
        new PlaneGeometry(runway.width * 0.55, runway.width),
        new MeshBasicMaterial({ color: colors.marker }),
      );
      threshold.position.set(
        runway.approach.x,
        runway.approach.y,
        LAYER.runway + 0.04,
      );
      threshold.rotation.z = angle;

      // Second channel for colour blindness: the runway's shape at the gate
      // the player aims for, on the same pale disc the aircraft badge uses.
      // The shape keeps the runway's dark tone, so it still carries the hue
      // for anyone who sees it, and the disc supplies the contrast the runway
      // itself cannot. Left world-upright, not runway-aligned, so the same
      // shape looks the same on every runway.
      const symbol = RUNWAY_SYMBOLS[runway.color];
      const symbolGeometry = regularPolygonGeometry(
        symbol.sides,
        symbol.rotation,
        runway.width * SYMBOL_SIZING.runwayThreshold,
      );
      if (symbolGeometry) {
        // Set one radius in from the gate, so the whole badge lands on tarmac
        // rather than half of it on the grass past the runway end.
        const discRadius = runway.width * SYMBOL_SIZING.runwayThresholdDisc;
        const badgeX = runway.approach.x + Math.cos(angle) * discRadius;
        const badgeY = runway.approach.y + Math.sin(angle) * discRadius;

        const disc = new Mesh(
          new CircleGeometry(discRadius, 20),
          new MeshBasicMaterial({ color: SYMBOL_BADGE_COLOR }),
        );
        disc.name = "runway-symbol-disc";
        disc.position.set(badgeX, badgeY, LAYER.runway + 0.05);
        disc.visible = this.symbolsVisible;
        this.runwaySymbols.push(disc);
        this.runwayGroup.add(disc);

        const symbolMesh = new Mesh(
          symbolGeometry,
          new MeshBasicMaterial({ color: colors.base }),
        );
        symbolMesh.name = "runway-symbol";
        symbolMesh.position.set(badgeX, badgeY, LAYER.runway + 0.06);
        symbolMesh.visible = this.symbolsVisible;
        this.runwaySymbols.push(symbolMesh);
        this.runwayGroup.add(symbolMesh);
      }

      for (let tick = 1; tick <= 4; tick++) {
        const t = tick / 5;
        const mark = new Mesh(
          new PlaneGeometry(runway.width * 0.35, runway.width * 0.14),
          new MeshBasicMaterial({
            color: colors.center,
            transparent: true,
            opacity: 0.9,
          }),
        );
        mark.position.set(
          runway.approach.x + (runway.end.x - runway.approach.x) * t,
          runway.approach.y + (runway.end.y - runway.approach.y) * t,
          LAYER.runway + 0.05,
        );
        mark.rotation.z = angle + Math.PI / 2;
        this.runwayGroup.add(mark);
      }

      this.runwayGroup.add(base, centerline, threshold);
    }
  }

  // ------------------------------------------------------------------ scenery

  buildScenery(map: AirportMap, nowSeconds: number): void {
    disposeChildren(this.sceneryGroup);
    this.sailboats = [];
    this.sailboatCurve = null;
    this.nextSailboatAt = Number.POSITIVE_INFINITY;
    this.sailboatsPausedAt = null;
    this.lastUpdateAt = null;
    this.trees = [];
    this.windsocks = [];
    this.shimmer = [];
    this.clouds = [];
    this.flocks = [];
    this.clock = 0;
    this.clockAt = null;

    const scenery = map.scenery;
    const colors = PALETTE.scenery;

    const grassMaterials: Record<GrassShade, MeshBasicMaterial> = {
      light: new MeshBasicMaterial({ color: colors.grassLight }),
      mid: new MeshBasicMaterial({ color: colors.grassMid }),
      dark: new MeshBasicMaterial({ color: colors.grassDark }),
    };

    for (const patch of scenery.grassPatches) {
      const geometry = polygonGeometry(patch.points);
      if (!geometry) {
        continue;
      }
      const mesh = new Mesh(geometry, grassMaterials[patch.shade]);
      mesh.position.z = LAYER.terrain;
      this.sceneryGroup.add(mesh);
    }

    if (scenery.dirtFields.length > 0) {
      const slab = new PlaneGeometry(1, 1);
      const dirtMaterial = new MeshBasicMaterial({ color: colors.dirt });
      for (const field of scenery.dirtFields) {
        const mesh = new Mesh(slab, dirtMaterial);
        mesh.name = "airport-dirt-field";
        mesh.position.set(field.x, field.y, LAYER.dirt);
        mesh.scale.set(field.width, field.depth, 1);
        this.sceneryGroup.add(mesh);
      }
    }

    if (scenery.stream) {
      this.buildStream(scenery.stream, nowSeconds);
    }

    this.buildTrees(map);
    this.buildBuildings(map);
    this.buildCloudShadows();
    this.buildFlocks();
    this.buildWindsocks(map);
  }

  /**
   * Soft shadows crossing the field.
   *
   * There is no sky in this projection, so the only way to show weather moving
   * over the airport is what it does to the ground.
   */
  private buildCloudShadows(): void {
    const disc = new CircleGeometry(1, 16);
    const material = new MeshBasicMaterial({
      color: PALETTE.scenery.cloudShadow,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
    });

    for (let index = 0; index < CLOUD_COUNT; index++) {
      const mesh = new Mesh(disc, material);
      mesh.name = "cloud-shadow";
      mesh.position.z = LAYER.terrain + 0.005;
      // Wide and shallow, and each one a different size, so a pass over the
      // runway does not read as a repeating pattern.
      const width = 46 + index * 13;
      mesh.scale.set(width, width * 0.52, 1);
      this.sceneryGroup.add(mesh);

      // All on the same wind, spread along it.
      this.clouds.push({
        object: mesh,
        dirX: 0.92,
        dirY: 0.39,
        originX: -CLOUD_SPAN / 2,
        originY: -110 + index * 52,
        offset: (index / CLOUD_COUNT) * CLOUD_SPAN,
        speed: CLOUD_SPEED * (0.8 + index * 0.1),
        span: CLOUD_SPAN,
      });
    }
  }

  /** Three birds to a flock, flapping, on their own looping line. */
  private buildFlocks(): void {
    const wingGeometry = new PlaneGeometry(1.5, 0.34);
    const material = new MeshBasicMaterial({
      color: PALETTE.scenery.bird,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    });

    for (let index = 0; index < BIRD_FLOCKS; index++) {
      const group = new Group();
      group.name = "bird-flock";
      const wings: Mesh[] = [];

      // A V: the leader ahead, two behind and to the sides.
      for (const [along, across] of [
        [0, 0],
        [-2.4, 1.7],
        [-2.4, -1.7],
      ] as const) {
        const bird = new Mesh(wingGeometry, material);
        bird.position.set(along, across, 0);
        group.add(bird);
        wings.push(bird);
      }

      const heading = 0.6 + index * 1.9;
      group.position.z = LAYER.aircraft + 6;
      this.sceneryGroup.add(group);

      this.flocks.push({
        group,
        wings,
        phase: index * 2.3,
        drift: {
          object: group,
          dirX: Math.cos(heading),
          dirY: Math.sin(heading),
          originX: -BIRD_SPAN / 2 - index * 40,
          originY: -80 + index * 90,
          offset: (index / BIRD_FLOCKS) * BIRD_SPAN,
          speed: BIRD_SPEED * (0.85 + index * 0.15),
          span: BIRD_SPAN,
        },
      });
    }
  }

  /** A sock at each threshold — the only wind the player can actually see. */
  private buildWindsocks(map: AirportMap): void {
    const colors = PALETTE.scenery;
    // Chunky on purpose: the canvas renders at 1/2.4 of CSS size, so a
    // realistically thin pole would land on less than a pixel and vanish.
    const poleGeometry = new CylinderGeometry(0.34, 0.44, 5.4, 5);
    const baseGeometry = new CircleGeometry(1.1, 10);
    const poleMaterial = new MeshLambertMaterial({
      color: colors.windsockPole,
      flatShading: true,
    });
    const sockGeometry = new ConeGeometry(0.9, 3.4, 6, 1, true);
    const lightMaterial = new MeshLambertMaterial({
      color: colors.windsockLight,
      flatShading: true,
      side: DoubleSide,
    });
    const darkMaterial = new MeshLambertMaterial({
      color: colors.windsockDark,
      flatShading: true,
      side: DoubleSide,
    });

    map.runways.forEach((runway, index) => {
      const axisX = runway.end.x - runway.approach.x;
      const axisY = runway.end.y - runway.approach.y;
      const length = Math.hypot(axisX, axisY) || 1;

      const group = new Group();
      group.name = "windsock";
      group.position.set(
        runway.approach.x - (axisY / length) * WINDSOCK_OFFSET,
        runway.approach.y + (axisX / length) * WINDSOCK_OFFSET,
        0,
      );

      const pole = new Mesh(poleGeometry, poleMaterial);
      pole.rotation.x = Math.PI / 2;
      pole.position.z = 2.7;
      group.add(pole);

      const base = new Mesh(
        baseGeometry,
        new MeshBasicMaterial({
          color: colors.shadow,
          transparent: true,
          opacity: 0.3,
          depthWrite: false,
        }),
      );
      base.position.set(0.5, 0.4, LAYER.sceneryShadow + 0.01);
      group.add(base);

      // The sock itself hangs off the top and is what swings; it is a child of
      // an arm so the swing pivots at the pole, not at the sock's middle.
      const arm = new Group();
      arm.name = "windsock-arm";
      arm.position.z = 5.1;
      for (const [offset, scale, material] of [
        [1.5, 0.95, darkMaterial],
        [4.1, 0.7, lightMaterial],
      ] as const) {
        const band = new Mesh(sockGeometry, material);
        // Cone points along +y by default; lay it down the arm, narrow end out.
        band.rotation.z = -Math.PI / 2;
        band.scale.setScalar(scale);
        band.position.set(offset, 0, 0);
        arm.add(band);
      }
      group.add(arm);

      this.sceneryGroup.add(group);
      this.windsocks.push({ group: arm, phase: index * 1.4 });
    });
  }

  private buildStream(
    stream: NonNullable<AirportMap["scenery"]["stream"]>,
    nowSeconds: number,
  ): void {
    const colors = PALETTE.scenery;
    const extended = extendPolyline(stream.points, RIVER_EXTEND);
    const course = resamplePolyline(extended, RIVER_SAMPLES);

    const bank = raggedRibbonGeometry(
      course,
      stream.width + RIVER_BANK_PADDING,
      RIVER_BANK_JITTER,
      1,
    );
    if (!bank) {
      return;
    }

    const bankMesh = new Mesh(
      bank,
      new MeshBasicMaterial({ color: colors.streamBank, side: DoubleSide }),
    );
    bankMesh.name = "river-bank";
    bankMesh.position.z = LAYER.stream;
    this.sceneryGroup.add(bankMesh);

    // Water is a chain of discs joined by quads: cheap, and the round joints
    // keep the bends smooth without a mitred ribbon.
    const waterMaterial = new MeshBasicMaterial({ color: colors.water });
    const quad = new PlaneGeometry(1, 1);
    const disc = new CircleGeometry(stream.width / 2, 12);

    for (let i = 0; i < course.length; i++) {
      const point = course[i]!;
      const joint = new Mesh(disc, waterMaterial);
      joint.name = "river-water";
      joint.position.set(point.x, point.y, LAYER.stream + 0.01);
      this.sceneryGroup.add(joint);

      if (i === course.length - 1) {
        continue;
      }
      const next = course[i + 1]!;
      const dx = next.x - point.x;
      const dy = next.y - point.y;
      const span = new Mesh(quad, waterMaterial);
      span.name = "river-water";
      span.position.set(
        (point.x + next.x) / 2,
        (point.y + next.y) / 2,
        LAYER.stream + 0.01,
      );
      span.scale.set(Math.hypot(dx, dy), stream.width, 1);
      span.rotation.z = Math.atan2(dy, dx);
      this.sceneryGroup.add(span);
    }

    // Shimmer bands: pale slivers that ride the current. They are positioned
    // along the same curve the boats use, so they follow every bend for free.
    const shimmerMaterial = new MeshBasicMaterial({
      color: colors.waterHighlight,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
    });
    for (let index = 0; index < SHIMMER_COUNT; index++) {
      const band = new Mesh(quad, shimmerMaterial);
      band.name = "river-shimmer";
      band.position.z = LAYER.stream + 0.02;
      band.scale.set(stream.width * 0.42, stream.width * 0.16, 1);
      this.sceneryGroup.add(band);
      this.shimmer.push(band);
    }

    if (extended.length >= 2) {
      this.sailboatCurve = new CatmullRomCurve3(
        extended.map(
          (point) => new Vector3(point.x, point.y, LAYER.stream + 0.1),
        ),
        false,
        "centripetal",
      );
      this.spawnSailboat(
        nowSeconds - SAILBOAT_DURATION * 0.36,
        SAILBOAT_DURATION,
        0.7,
      );
      this.nextSailboatAt = nowSeconds + SAILBOAT_INTERVAL;
    }
  }

  private buildTrees(map: AirportMap): void {
    const colors = PALETTE.scenery;
    const shadowGeometry = new CircleGeometry(1, 8);
    const trunkGeometry = new CylinderGeometry(0.42, 0.62, 2.8, 5);
    const crownGeometry = new IcosahedronGeometry(2.5, 0);
    const tuftGeometry = new PlaneGeometry(1.4, 0.34);

    const shadowMaterial = new MeshBasicMaterial({
      color: colors.shadow,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    });
    const trunkMaterial = new MeshLambertMaterial({
      color: colors.treeTrunk,
      flatShading: true,
    });
    const lightLeaves = new MeshLambertMaterial({
      color: colors.treeLight,
      flatShading: true,
    });
    const darkLeaves = new MeshLambertMaterial({
      color: colors.treeDark,
      flatShading: true,
    });
    const tuftMaterial = new MeshBasicMaterial({ color: colors.grassTuft });

    map.scenery.trees.forEach((tree, index) => {
      const group = new Group();
      group.name = "airport-tree";
      group.position.set(tree.x, tree.y, 0);
      group.scale.setScalar(tree.scale ?? 1);

      const shadow = new Mesh(shadowGeometry, shadowMaterial);
      shadow.position.set(1.35, 1.05, LAYER.sceneryShadow);
      shadow.scale.set(3.2, 1.85, 1);

      const trunk = new Mesh(trunkGeometry, trunkMaterial);
      trunk.rotation.x = Math.PI / 2;
      trunk.position.z = 1.4;

      const crown = new Mesh(
        crownGeometry,
        index % 2 ? lightLeaves : darkLeaves,
      );
      crown.position.z = 4.05;
      crown.scale.set(1, 1, 0.92);

      const bud = new Mesh(crownGeometry, index % 2 ? darkLeaves : lightLeaves);
      bud.position.set(0.72, -0.42, 4.75);
      bud.scale.setScalar(0.62);

      for (let i = 0; i < 2; i++) {
        const tuft = new Mesh(tuftGeometry, tuftMaterial);
        const angle = index * 1.7 + i * 1.9;
        tuft.position.set(
          Math.cos(angle) * 3.2,
          Math.sin(angle) * 2.7,
          LAYER.sceneryShadow + 0.005,
        );
        tuft.rotation.z = angle;
        group.add(tuft);
      }

      group.add(shadow, trunk, crown, bud);
      this.sceneryGroup.add(group);
      // The whole tree leans, pivoting at the base — cheaper than animating the
      // crown alone, and at a degree and a half the trunk hides the cheat.
      this.trees.push({ group, phase: index * 0.83 });
    });
  }

  private buildBuildings(map: AirportMap): void {
    const colors = PALETTE.scenery;
    const box = new BoxGeometry(1, 1, 1);
    const wallMaterial = new MeshLambertMaterial({
      color: colors.building,
      flatShading: true,
    });
    const roofMaterial = new MeshLambertMaterial({
      color: colors.buildingRoof,
      flatShading: true,
    });
    const darkMaterial = new MeshLambertMaterial({
      color: colors.buildingDark,
      flatShading: true,
    });
    const windowMaterial = new MeshBasicMaterial({ color: colors.window });
    const shadowMaterial = new MeshBasicMaterial({
      color: colors.shadow,
      transparent: true,
      opacity: 0.24,
      depthWrite: false,
    });

    for (const building of map.scenery.buildings) {
      const group = new Group();
      group.name = "airport-building";
      group.userData.kind = building.kind;
      group.position.set(building.x, building.y, 0);
      group.rotation.z = building.rotation ?? 0;

      const shadow = new Mesh(box, shadowMaterial);
      shadow.scale.set(building.width + 1, building.depth + 1, 0.08);
      shadow.position.set(1.25, 1.1, LAYER.sceneryShadow + 0.01);

      if (building.kind === "tower") {
        const shaft = new Mesh(box, wallMaterial);
        shaft.scale.set(
          building.width * 0.42,
          building.depth * 0.42,
          building.height * 0.68,
        );
        shaft.position.z = building.height * 0.34;

        const cabin = new Mesh(box, windowMaterial);
        cabin.scale.set(building.width, building.depth, building.height * 0.2);
        cabin.position.z = building.height * 0.75;

        const cap = new Mesh(box, darkMaterial);
        cap.scale.set(building.width + 0.8, building.depth + 0.8, 0.42);
        cap.position.z = building.height * 0.87;

        group.add(shaft, cabin, cap);
      } else {
        const walls = new Mesh(box, wallMaterial);
        walls.scale.set(building.width, building.depth, building.height);
        walls.position.z = building.height / 2;

        const roof = new Mesh(
          box,
          building.kind === "hangar" ? darkMaterial : roofMaterial,
        );
        roof.scale.set(building.width + 0.8, building.depth + 0.8, 0.42);
        roof.position.z = building.height + 0.21;

        const sideWindows = new Mesh(box, windowMaterial);
        sideWindows.scale.set(
          0.16,
          building.depth * 0.62,
          building.height * 0.22,
        );
        sideWindows.position.set(
          building.width / 2 + 0.09,
          -0.1,
          building.height * 0.58,
        );

        const frontWindows = new Mesh(box, windowMaterial);
        frontWindows.scale.set(
          building.width * 0.65,
          0.16,
          building.height * 0.22,
        );
        frontWindows.position.set(
          0,
          -building.depth / 2 - 0.09,
          building.height * 0.58,
        );

        group.add(walls, roof, sideWindows, frontWindows);

        if (building.kind === "terminal") {
          for (const offset of [-5.5, 0, 5.5]) {
            const vent = new Mesh(box, roofMaterial);
            vent.scale.set(2.1, 2.1, 0.65);
            vent.position.set(offset, 0, building.height + 0.72);
            group.add(vent);
          }
        }
      }

      group.add(shadow);
      this.sceneryGroup.add(group);
    }
  }

  // ---------------------------------------------------------------- sailboats

  private createSailboat(): Group {
    const group = new Group();
    group.name = "river-sailboat";
    const colors = PALETTE.sailboat;

    const hull = new Mesh(
      new ConeGeometry(1.25, 4.8, 5),
      new MeshLambertMaterial({ color: colors.hull, flatShading: true }),
    );
    hull.rotation.z = -Math.PI / 2;
    hull.scale.z = 0.55;
    hull.position.z = 0.72;

    const deck = new Mesh(
      new BoxGeometry(2.8, 1.55, 0.34),
      new MeshLambertMaterial({ color: colors.deck, flatShading: true }),
    );
    deck.position.set(-0.35, 0, 1.08);

    const mast = new Mesh(
      new CylinderGeometry(0.2, 0.23, 4.8, 6),
      new MeshLambertMaterial({
        color: colors.mast,
        flatShading: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    mast.name = "sailboat-mast";
    mast.rotation.x = Math.PI / 2;
    mast.position.set(-0.15, 0, 3.2);
    mast.renderOrder = 4;

    const sailShape = new Shape();
    sailShape.moveTo(0, 0.18);
    sailShape.lineTo(0, 4.2);
    sailShape.lineTo(2.35, 0.45);
    sailShape.closePath();
    const sail = new Mesh(
      new ShapeGeometry(sailShape),
      new MeshLambertMaterial({
        color: colors.sail,
        flatShading: true,
        side: DoubleSide,
      }),
    );
    sail.name = "sailboat-sail";
    sail.rotation.x = Math.PI / 2;
    sail.position.set(-0.08, 0, 1.18);
    sail.renderOrder = 3;

    const wakeMaterial = new MeshBasicMaterial({
      color: colors.wake,
      transparent: true,
      opacity: 0.62,
      depthWrite: false,
      side: DoubleSide,
    });
    for (const side of [-1, 1]) {
      const wake = new Mesh(new PlaneGeometry(4.2, 0.28), wakeMaterial);
      wake.position.set(-3.1, side * 1.3, 0.2);
      wake.rotation.z = side * -0.24;
      group.add(wake);
    }

    group.add(hull, deck, mast, sail);
    group.scale.setScalar(1.08);
    return group;
  }

  private spawnSailboat(
    startedAt: number,
    duration: number,
    phase: number,
  ): void {
    const group = this.createSailboat();
    this.sailboats.push({ group, startedAt, duration, phase });
    this.sceneryGroup.add(group);
  }

  /**
   * Animate the background; `paused` freezes all of it in place.
   *
   * Two clocks, for two reasons. The trees, water, clouds, birds and windsocks
   * are functions of an accumulated `clock`, so not advancing it *is* the
   * pause. The boats carry a start time instead, so they get their elapsed
   * time shifted forward when the clock jumps.
   */
  update(nowSeconds: number, paused = false): void {
    const delta =
      this.clockAt === null
        ? 0
        : Math.max(0, Math.min(MAX_SCENERY_GAP, nowSeconds - this.clockAt));
    this.clockAt = nowSeconds;
    if (!paused) {
      this.clock += delta;
      this.animateBackground(this.clock);
    }

    this.updateSailboats(nowSeconds, paused);
  }

  private animateBackground(clock: number): void {
    for (const tree of this.trees) {
      const lean = Math.sin(clock * TREE_SWAY_RATE + tree.phase);
      // Two axes, out of phase, so it circles slightly instead of rocking flat.
      tree.group.rotation.x = lean * TREE_SWAY_RADIANS;
      tree.group.rotation.y =
        Math.cos(clock * TREE_SWAY_RATE * 0.7 + tree.phase) *
        TREE_SWAY_RADIANS *
        0.6;
    }

    for (const sock of this.windsocks) {
      sock.group.rotation.z =
        Math.sin(clock * WINDSOCK_RATE + sock.phase) * WINDSOCK_SWAY +
        Math.sin(clock * WINDSOCK_RATE * 2.3 + sock.phase) *
          WINDSOCK_SWAY *
          0.3;
    }

    if (this.sailboatCurve) {
      for (let index = 0; index < this.shimmer.length; index++) {
        const band = this.shimmer[index]!;
        const t = (index / this.shimmer.length + clock * SHIMMER_SPEED) % 1;
        const point = this.sailboatCurve.getPointAt(t);
        const tangent = this.sailboatCurve.getTangentAt(t).normalize();
        band.position.x = point.x;
        band.position.y = point.y;
        band.rotation.z = Math.atan2(tangent.y, tangent.x);
        // Fade in and out over the run so a band never pops at the wrap.
        const material = band.material as MeshBasicMaterial;
        material.opacity = 0.34 * Math.sin(Math.PI * t) ** 0.6;
      }
    }

    for (const cloud of this.clouds) {
      const travelled = (cloud.offset + clock * cloud.speed) % cloud.span;
      cloud.object.position.x = cloud.originX + cloud.dirX * travelled;
      cloud.object.position.y = cloud.originY + cloud.dirY * travelled;
    }

    for (const flock of this.flocks) {
      const drift = flock.drift;
      const travelled = (drift.offset + clock * drift.speed) % drift.span;
      drift.object.position.x = drift.originX + drift.dirX * travelled;
      drift.object.position.y = drift.originY + drift.dirY * travelled;
      drift.object.rotation.z = Math.atan2(drift.dirY, drift.dirX);

      // Flapping is a squash of the wing quad: at this size a real wing shape
      // would be one pixel either way.
      for (let index = 0; index < flock.wings.length; index++) {
        const wing = flock.wings[index]!;
        const beat = Math.sin(
          clock * BIRD_FLAP_RATE + flock.phase + index * 0.7,
        );
        wing.scale.set(1, 0.55 + Math.abs(beat) * 0.75, 1);
      }
    }
  }

  private updateSailboats(nowSeconds: number, paused: boolean): void {
    if (!this.sailboatCurve) {
      return;
    }

    if (paused) {
      this.sailboatsPausedAt ??= nowSeconds;
      return;
    }

    // Two ways the clock can jump: an explicit pause, and a hidden tab, which
    // stops rAF without ever setting `paused`. Both are compensated the same
    // way — shift the boats forward so they resume in place instead of
    // teleporting down the river.
    const resumingFrom =
      this.sailboatsPausedAt ??
      (this.lastUpdateAt !== null &&
      nowSeconds - this.lastUpdateAt > MAX_SCENERY_GAP
        ? this.lastUpdateAt
        : null);

    if (resumingFrom !== null) {
      const shift = nowSeconds - resumingFrom;
      for (const boat of this.sailboats) {
        boat.startedAt += shift;
      }
      if (Number.isFinite(this.nextSailboatAt)) {
        this.nextSailboatAt += shift;
      }
      this.sailboatsPausedAt = null;
    }
    this.lastUpdateAt = nowSeconds;

    if (nowSeconds >= this.nextSailboatAt) {
      this.spawnSailboat(nowSeconds, SAILBOAT_DURATION, 0);
      this.nextSailboatAt = nowSeconds + SAILBOAT_INTERVAL;
    }

    for (let i = this.sailboats.length - 1; i >= 0; i--) {
      const boat = this.sailboats[i]!;
      const progress = (nowSeconds - boat.startedAt) / boat.duration;
      if (progress >= 1) {
        this.sceneryGroup.remove(boat.group);
        disposeChildren(boat.group);
        this.sailboats.splice(i, 1);
        continue;
      }
      if (progress < 0) {
        continue;
      }

      const point = this.sailboatCurve.getPointAt(progress);
      const tangent = this.sailboatCurve.getTangentAt(progress).normalize();
      const time = nowSeconds + boat.phase;
      const sway = Math.sin(time * 0.34) * 0.55 + Math.sin(time * 0.13) * 0.22;
      point.x += -tangent.y * sway;
      point.y += tangent.x * sway;
      boat.group.position.copy(point);
      boat.group.rotation.z =
        Math.atan2(tangent.y, tangent.x) +
        Math.sin(time * 0.34) * 0.13 +
        Math.sin(time * 0.17) * 0.045;
    }
  }

  dispose(): void {
    disposeChildren(this.sceneryGroup);
    disposeChildren(this.runwayGroup);
    this.runwaySymbols = [];
    this.ground.geometry.dispose();
    (this.ground.material as MeshBasicMaterial).dispose();
  }
}
